import React, { useState, useEffect } from 'react';
import { getAttachmentUrl, getCachedAttachmentUrl, removeAttachmentFromCache } from '../utils/attachmentUtils';
import { Download, ExternalLink, FileText, Image, File } from 'lucide-react';

const Attachment = ({ 
  attachment, 
  size = "md", 
  className = "", 
  showPreview = true,
  onClick = null,
  showDownload = true,
  showFileInfo = false
}) => {
  const [attachmentUrl, setAttachmentUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Size classes
  const sizeClasses = {
    xs: "h-16 w-16",
    sm: "h-20 w-20", 
    md: "h-24 w-24",
    lg: "h-32 w-32",
    xl: "h-40 w-40",
    auto: ""
  };

  const iconSizes = {
    xs: "h-6 w-6",
    sm: "h-8 w-8",
    md: "h-10 w-10",
    lg: "h-12 w-12",
    xl: "h-16 w-16"
  };

  useEffect(() => {
    const loadAttachmentUrl = async () => {
      // Handle both new filename field and legacy blob_name field
      const identifier = attachment?.filename || attachment?.blob_name;
      if (!identifier) {
        setIsLoading(false);
        return;
      }

      try {
        // Try to get cached URL first for immediate display
        const cachedUrl = getCachedAttachmentUrl(identifier);
        if (cachedUrl) {
          setAttachmentUrl(cachedUrl);
          setIsLoading(false);
          return;
        }

        // Get fresh URL from backend using identifier
        const freshUrl = await getAttachmentUrl(identifier);
        if (freshUrl) {
          setAttachmentUrl(freshUrl);
          setHasError(false);
        } else {
          setHasError(true);
        }
      } catch (error) {
        console.error('Failed to load attachment URL:', error);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadAttachmentUrl();
  }, [attachment?.filename, attachment?.blob_name]);

  const handleClick = () => {
    if (onClick && attachmentUrl) {
      onClick(attachment, attachmentUrl);
    }
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    if (attachmentUrl) {
      const link = document.createElement('a');
      link.href = attachmentUrl;
      link.download = attachment.filename || 'attachment';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleOpenInNewTab = (e) => {
    e.stopPropagation();
    if (attachmentUrl) {
      window.open(attachmentUrl, '_blank');
    }
  };

  const renderFileIcon = () => {
    if (attachment?.content_type?.startsWith('image/')) {
      return <Image className={`${iconSizes[size]} text-blue-500`} />;
    } else if (attachment?.content_type === 'application/pdf') {
      return <FileText className={`${iconSizes[size]} text-red-500`} />;
    } else if (attachment?.content_type?.includes('word')) {
      return <FileText className={`${iconSizes[size]} text-blue-500`} />;
    } else if (attachment?.content_type?.includes('excel') || attachment?.content_type?.includes('spreadsheet')) {
      return <FileText className={`${iconSizes[size]} text-green-500`} />;
    } else {
      return <File className={`${iconSizes[size]} text-gray-500`} />;
    }
  };

  const renderFilePreview = () => {
    if (attachment?.content_type?.startsWith('image/')) {
      return (
        <img
          src={attachmentUrl}
          alt={attachment.filename || 'Attachment'}
          className={size === 'auto' ? 'max-w-full h-auto rounded-lg' : 'w-full h-full object-cover'}
          onError={() => setHasError(true)}
        />
      );
    } else if (attachment?.content_type === 'application/pdf') {
      return (
        <div className="w-full h-full flex items-center justify-center bg-red-50">
          <div className="text-center">
            <div className="text-red-500 text-4xl mb-2">📄</div>
            <div className="text-red-700 text-xs font-medium">PDF</div>
          </div>
        </div>
      );
    } else if (attachment?.content_type?.includes('word')) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-blue-50">
          <div className="text-center">
            <div className="text-blue-500 text-4xl mb-2">📝</div>
            <div className="text-blue-700 text-xs font-medium">Word</div>
          </div>
        </div>
      );
    } else if (attachment?.content_type?.includes('excel') || attachment?.content_type?.includes('spreadsheet')) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-green-50">
          <div className="text-center">
            <div className="text-green-500 text-4xl mb-2">📊</div>
            <div className="text-green-700 text-xs font-medium">Excel</div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="w-full h-full flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="text-gray-500 text-4xl mb-2">📎</div>
            <div className="text-gray-700 text-xs font-medium">File</div>
          </div>
        </div>
      );
    }
  };

  if (isLoading) {
    return (
      <div className={`${sizeClasses[size]} ${className} bg-gray-100 rounded-lg border flex items-center justify-center animate-pulse`}>
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400"></div>
      </div>
    );
  }

  if (hasError || !attachmentUrl) {
    return (
      <div className={`${sizeClasses[size]} ${className} bg-gray-100 rounded-lg border flex items-center justify-center`}>
        <div className="text-center">
          <div className="text-gray-400 text-4xl mb-2">❌</div>
          <div className="text-gray-500 text-xs">Failed to load</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${sizeClasses[size]} ${className} relative group ${size === 'auto' ? 'inline-block' : ''}`}>
      {/* File Preview */}
      <div 
        className={`${size === 'auto' ? '' : 'w-full h-full'} ${attachment?.content_type?.startsWith('image/') ? 'rounded-lg' : 'bg-gray-100 rounded-lg border'} overflow-hidden hover:shadow-md transition-shadow cursor-pointer ${onClick ? 'hover:scale-105' : ''}`}
        onClick={handleClick}
      >
        {showPreview ? renderFilePreview() : renderFileIcon()}
      </div>

      {/* Action Buttons */}
      <div className={`absolute ${size === 'auto' ? 'inset-0' : 'inset-0'} bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100`}>
        <div className="flex gap-2">
          {showDownload && (
            <button
              onClick={handleDownload}
              className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
              title="Download"
            >
              <Download className="h-4 w-4 text-gray-700" />
            </button>
          )}
          <button
            onClick={handleOpenInNewTab}
            className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
            title="Open in new tab"
          >
            <ExternalLink className="h-4 w-4 text-gray-700" />
          </button>
        </div>
      </div>

      {/* File Info */}
      {showFileInfo && (
        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white text-xs p-2 truncate">
          {attachment.filename || 'Unknown file'}
        </div>
      )}
    </div>
  );
};

export default Attachment;
